import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { dashboardApi } from '../services/tenantApi';

const DemandForecastChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userContext, setUserContext] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(false);

  // Function to get bar color based on demand score
  const getBarColor = (score) => {
    if (score >= 90) return '#dc2626'; // Deep red (peak)
    if (score >= 70) return '#fecaca'; // Light red (high)
    return '#3b81f6'; // Blue (typical)
  };

  // Function to get demand level text
  const getDemandLevel = (score) => {
    if (score >= 90) return 'Peak';
    if (score >= 70) return 'High';
    return 'Typical';
  };

  // Generate mock data for 90 days (will be replaced with API call)
  const generateForecastData = () => {
    const data = [];
    const startDate = new Date();
    
    for (let i = 0; i < 90; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      // Generate realistic demand scores with seasonal patterns
      const baseScore = 60;
      const seasonalVariation = Math.sin((i / 90) * Math.PI * 2) * 15;
      const weeklyPattern = Math.sin((i / 7) * Math.PI * 2) * 8;
      const randomVariation = (Math.random() - 0.5) * 10;
      
      const score = Math.max(20, Math.min(95, 
        baseScore + seasonalVariation + weeklyPattern + randomVariation
      ));
      
      const roundedScore = Math.round(score * 10) / 10;
      data.push({
        date: date.toISOString().split('T')[0],
        formattedDate: date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }),
        score: roundedScore,
        color: getBarColor(roundedScore),
        level: getDemandLevel(roundedScore)
      });
    }
    
    return data;
  };

  // Generate 90-day date range (same as DemandDriverAnalysis)
  const get90DayRange = () => {
    const today = new Date();
    const start = today.toISOString().split('T')[0];
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + 90);
    const end = endDate.toISOString().split('T')[0];
    return { start, end };
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setNeedsSetup(false);
        
        // First, get user context to check if hotel is configured
        const context = await dashboardApi.getUserContext();
        setUserContext(context);
        
        // Check if user has hotel configured (check for hotel_id in context)
        if (!context?.hotel_id) {
          console.log('🏨 No hotel configured - showing setup message');
          setNeedsSetup(true);
          setLoading(false);
          return;
        }
        
        // Use hotel_city from dashboard context (no extra fetch needed)
        const location = context.hotel_city?.toLowerCase() || 'bali';
        console.log('🏨 Using hotel for demand:', { hotel_id: context.hotel_id, location });
        
        // Use the public demand API with hotel's location
        const { start, end } = get90DayRange();
        console.log('🔍 DemandForecast: Fetching 90-day data:', { start, end, location });
        
        // Call public API with dynamic location
        const response = await fetch(`/api/demand?location=${encodeURIComponent(location)}&days=90`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const apiData = await response.json();
        console.log('📊 DemandForecast: API response structure:', {
          hasItems: !!apiData?.items,
          itemsLength: apiData?.items?.length || 0,
          hasDemandScores: !!apiData?.demand_scores,
          demandScoresLength: apiData?.demand_scores?.length || 0,
          sampleItem: apiData?.items?.[0] || apiData?.demand_scores?.[0] || null
        });
        
        // Use the SAME data processing logic as DemandDriverAnalysis
        let demandScores = [];
        if (apiData?.demand_scores) {
          demandScores = apiData.demand_scores;
          console.log('🔍 DemandForecast: Using demand_scores array:', demandScores.length, 'items');
        } else {
          throw new Error('No demand_scores in API response');
        }
        
        if (demandScores && demandScores.length > 0) {
          // Process real demand scores using SAME field names as DemandDriverAnalysis
          const processedData = demandScores.map((item, index) => {
            const date = new Date(item.date);
            // Use the SAME field as DemandDriverAnalysis: score (unified API)
            const realScore = item.score || 0;
            const roundedScore = Math.round(realScore * 10) / 10;
            
            if (index < 5) { // Log first 5 for debugging
              console.log(`📊 DemandForecast Day ${index + 1} (${item.date}):`, {
                realScore,
                roundedScore,
                apiStructure: Object.keys(item)
              });
            }
            
            return {
              date: item.date,
              formattedDate: date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric' 
              }),
              score: roundedScore,
              color: getBarColor(roundedScore),
              level: getDemandLevel(roundedScore)
            };
          });
          
          console.log('✅ DemandForecast: Real demand data loaded:', processedData.length, 'days');
          setChartData(processedData);
        } else {
          throw new Error('No demand scores available');
        }
      } catch (err) {
        console.error('Error loading demand forecast:', err);
        // NO FALLBACK - Leave empty if no API data
        setChartData([]);
        setError('Please setup your hotel to see data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 mb-1">{label}</p>
          <p className="text-sm text-blue-600">
            Demand Score: <span className="font-semibold">{data.score}</span>
          </p>
          <p className="text-sm font-medium" style={{ color: data.color }}>
            Level: {data.level}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">90-Day Demand Forecast</h3>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading demand forecast...</div>
        </div>
      </div>
    );
  }

  if (needsSetup) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">90-Day Demand Forecast</h3>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <div className="text-6xl mb-4">🏨</div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">Setup Required</h4>
          <p className="text-gray-600 mb-4 max-w-md">
            Configure your hotel details to see demand forecasts for your location.
          </p>
          <button
            onClick={() => window.location.href = '/app/onboarding'}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Setup Hotel
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">90-Day Demand Forecast</h3>
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">Error loading forecast: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">
          90-Day Demand Forecast
        </h2>
        <p className="text-muted-foreground">
          Predicted local demand index scores for the next 90 days with color-coded demand levels
        </p>
      </div>
      
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData} 
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis 
              dataKey="formattedDate" 
              stroke="#64748b"
              fontSize={12}
              tickLine={false}
              interval="preserveStartEnd"
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              stroke="#64748b"
              fontSize={12}
              tickLine={false}
              domain={[0, 100]}
              label={{ 
                value: 'Demand Index Score', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle' }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Color-coded bars */}
            <Bar dataKey="score">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Color-coded Legend */}
      <div className="mt-4 flex items-center justify-center space-x-8 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#3b81f6' }}></div>
          <span className="text-gray-600">Typical (0-69)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#fecaca' }}></div>
          <span className="text-gray-600">High (70-89)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#dc2626' }}></div>
          <span className="text-gray-600">Peak (90-100)</span>
        </div>
      </div>
    </div>
  );
};

export default DemandForecastChart;
